// This file configures the initialization of Sentry on the client.
// The added config here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

// Load polyfills first before any other imports
;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/:slug\",\"regex\":\"^/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/articles/:category\",\"regex\":\"^/articles/([^/]+)$\",\"paramNames\":[\"category\"],\"hasOptionalPrefix\":false},{\"path\":\"/articles/:category/:slug\",\"regex\":\"^/articles/([^/]+)/([^/]+)$\",\"paramNames\":[\"category\",\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/authors/:slug\",\"regex\":\"^/authors/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/faq/:category\",\"regex\":\"^/faq/([^/]+)$\",\"paramNames\":[\"category\"],\"hasOptionalPrefix\":false},{\"path\":\"/item/:id*?\",\"regex\":\"^/item(?:/(.*))?$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/profile/messages/:id\",\"regex\":\"^/profile/messages/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/profile/my-vehicles/:id\",\"regex\":\"^/profile/my-vehicles/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/retailers/:slug\",\"regex\":\"^/retailers/([^/]+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/search/:slug*?\",\"regex\":\"^/search(?:/(.*))?$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/sell-to-dealer/:slug*\",\"regex\":\"^/sell-to-dealer/(.+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false},{\"path\":\"/sell-to-dealer/created/:id\",\"regex\":\"^/sell-to-dealer/created/([^/]+)$\",\"paramNames\":[\"id\"],\"hasOptionalPrefix\":false},{\"path\":\"/valuate-vehicle/:slug*\",\"regex\":\"^/valuate-vehicle/(.+)$\",\"paramNames\":[\"slug\"],\"hasOptionalPrefix\":false}],\"staticRoutes\":[{\"path\":\"/\"},{\"path\":\"/articles\"},{\"path\":\"/articles/search\"},{\"path\":\"/auth/signin\"},{\"path\":\"/authors\"},{\"path\":\"/faq\"},{\"path\":\"/faq/search\"},{\"path\":\"/oidc/remove-account\"},{\"path\":\"/oidc/signin\"},{\"path\":\"/oidc/signout\"},{\"path\":\"/profile\"},{\"path\":\"/profile/account\"},{\"path\":\"/profile/assignments\"},{\"path\":\"/profile/messages\"},{\"path\":\"/profile/my-vehicles\"},{\"path\":\"/profile/search-subscriptions\"},{\"path\":\"/profile/vehicle-subscriptions\"},{\"path\":\"/retailers\"},{\"path\":\"/sell-to-dealer\"},{\"path\":\"/sentry-example-page\"},{\"path\":\"/valuate-vehicle\"}],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.2.6";import './polyfills/bigint';
import './polyfills/structured-clone';

import * as Sentry from '@sentry/nextjs';

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
  environment: process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT,
  release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,

  // Disable performance monitoring (only track errors and sessions)
  tracesSampleRate: 0,

  // Disable logs to reduce quota usage
  enableLogs: false,

  // Disable session tracking to reduce traffic and I/O pool usage.
  // Add captureConsoleIntegration so React 19's recoverable hydration errors
  // (logged via console.error and never thrown) surface in Sentry. beforeSend
  // narrows the captured console.error events to React error codes only.
  integrations: (defaultIntegrations) => {
    return [
      ...defaultIntegrations.filter((integration) => integration.name !== 'BrowserSession'),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ];
  },

  // Disable SDK telemetry reports
  sendClientReports: false,

  // Use tunnel to avoid ad blockers
  tunnel: '/api/s/e',

  // Setting this option to true will print useful information to the console while you're setting up Sentry.
  debug: false,

  // Note: thirdPartyErrorFilterIntegration is not used because it requires webpack plugin
  // which doesn't work with Turbopack. We use manual filtering in beforeSend instead.

  // Filter out noise
  ignoreErrors: [
    // Browser extensions
    'top.GLOBALS',
    'chrome-extension://',
    'moz-extension://',
    // Browser extension runtime errors
    'runtime.sendMessage',
    /Invalid call to runtime\.sendMessage/,
    // Network errors
    'NetworkError',
    'Network request failed',
    'Failed to fetch',
    // Safari/iOS equivalent of "Failed to fetch" — a generic network-level
    // fetch failure (offline, dropped connection, backgrounded tab waking on a
    // changed network). Not an app bug and not actionable; no stack trace.
    'Load failed',
    // Another generic network-level fetch failure variant. Anchored (case-
    // insensitive) so it stays exact — the 'NetworkError' entry above does not
    // match it: different word break and casing ("network error" vs "NetworkError").
    /^network error$/i,
    // User cancelled requests
    'AbortError',
    // React Native WebView errors (https://github.com/facebook/react/issues/34053)
    'Error invoking postMessage: Java object is gone',
    // Facebook Android WebView errors - Facebook's injected scripts fail when Java bridge is destroyed
    // Occurs when users navigate away or close the page in Facebook in-app browser
    // https://github.com/getsentry/sentry-javascript/issues/2744
    'Error invoking enableDidUserTypeOnKeyboardLogging: Java object is gone',
    /Java object is gone/i,
    // Broad filter for all Java bridge errors (space after "java" avoids matching "javascript")
    /java /i,
    // iOS WebKit WebView errors - scripts trying to access unavailable message handlers
    // Common in Instagram, Facebook, and other in-app browsers
    // https://github.com/getsentry/sentry-javascript/issues/3040
    /undefined is not an object \(evaluating 'window\.webkit\.messageHandlers/,
    /window\.webkit\.messageHandlers is undefined/,
    /Cannot read property 'messageHandlers' of undefined/,
    // iOS WKWebView torn down (user navigated away / app backgrounded) while a
    // JS→native bridge message was still in flight. A platform-level error from
    // Chrome iOS / in-app browsers — not app code and not fixable on our side.
    'WKWebView was deallocated',
    // Third-party scripts (GTM, etc.) trying to access undefined globals
    /Can't find variable: wayke/,
    /wayke is not defined/,
    // Third-party tracking pixels not loaded (TikTok, Facebook, etc.)
    'ttq is not defined',
    /Can't find variable: ttq/,
    'fbq is not defined',
    'gtag is not defined',
    // Google reCAPTCHA timeout errors - known issue, doesn't affect functionality
    // https://github.com/dozoisch/react-google-recaptcha/issues/103
    // https://github.com/google/recaptcha/issues/269
    'Timeout',
    /Timeout \([a-z]\)/i,
    /recaptcha.*timeout/i,
    // Google Maps' own JS does cross-frame property access on its map-tile
    // iframe; the browser blocks it and surfaces a SecurityError. The stack is
    // entirely inside the Maps bundle (StaticMap component on dealer pages) —
    // no app code is involved and there is nothing to fix on our side.
    /^Blocked a frame with origin/,
    // Cookie-Script's own consent-banner script removes its banner node from a
    // requestAnimationFrame callback; by then React has already removed it, so
    // the node (or its parentNode) is null. Entirely inside the Cookie-Script
    // bundle — a third-party bug, not fixable on our side. Both engine phrasings
    // are listed (WebKit, then Blink) — the same crash reads differently per
    // engine. Our code never calls `removeChild` on a nullable object (the only
    // removeChild we make is `document.body.removeChild`, and document.body is
    // never null), so a null-removeChild deref cannot be one of ours.
    /null is not an object \(evaluating '\w+\.parentNode\.removeChild'\)/,
    /Cannot read propert(?:y|ies) .*'removeChild'/,
    // A third-party tracking/ads script (the page fires GTM, Google Ads, Stape
    // and Clarity pings around this point) rejects a background request with a
    // raw "You don't have permissions. Reference number: <id>" string. It is a
    // Non-Error rejection with no stack, and the phrase appears nowhere in our
    // source — not our code, and our fetch layer always wraps errors in Error
    // subclasses, so this cannot be one of ours.
    /You don.t have permissions/,
    // Browser page translation (Chrome built-in / Google Translate) crashes React.
    // The translator wraps text nodes in <font> elements, so React's commit phase
    // calls removeChild/insertBefore against a node that is no longer a direct
    // child and the DOM throws. Not caused by our code and not fixable from React.
    // https://github.com/facebook/react/issues/11538
    "Failed to execute 'removeChild' on 'Node'",
    "Failed to execute 'insertBefore' on 'Node'",
    // The same crash on WebKit/iOS, where the NotFoundError DOMException is
    // phrased differently ("NotFoundError: The object can not be found here").
    'The object can not be found here',
    // Failed CSS/JS chunk loads. Next.js loads chunks via <link>/<script> tags and
    // wraps them in a promise; an onerror fires a raw DOM Event that surfaces as an
    // uncaught rejection with no stack. Overwhelmingly flaky cross-border network
    // (e.g. CDN requests from China, Baidu mobile proxy) — not an actionable bug.
    // Scoped to the exact synthesized message to avoid hiding genuine non-Error
    // rejections in our own code.
    'Event `Event` (type=error) captured as promise rejection',
    // Bad JS chunk delivery. A <script> that arrives truncated, or an HTML error
    // page served by the CDN for a missing chunk hash (common right after a
    // deploy), fails to parse — the browser reports SyntaxError "Unexpected end
    // of input" or "Invalid or unexpected token". This is a network/CDN delivery
    // failure (overwhelmingly bot/crawler traffic on /objekt/), not an app bug,
    // and is not fixable in code. The JSON.parse variants ("Unexpected end of
    // JSON input" / "Unexpected token ... in JSON") are phrased differently and
    // are not matched — genuine JSON parse errors are still reported.
    'Unexpected end of input',
    'Invalid or unexpected token',
    // RSC stream aborted before completion. Next.js prefetches and streams React
    // Server Component payloads; the stream is cut whenever the user navigates
    // away, a prefetch is cancelled, or the connection drops. Next.js reports it
    // via reportGlobalError but recovers — not an app bug. Anchored so it cannot
    // match a longer message that merely contains this phrase.
    /^Connection closed\.$/,
  ],

  beforeSend(event, _hint) {
    // Filter reCAPTCHA timeout errors from Google's script
    // These are internal false positives that don't affect functionality
    if (event.exception?.values?.[0]) {
      const errorMessage = event.exception.values[0].value || '';
      const stacktrace = event.exception.values[0].stacktrace;

      // Check if this is a reCAPTCHA timeout error
      if (
        errorMessage.includes('Timeout') &&
        stacktrace?.frames?.some((frame) => {
          const filename = frame.filename || '';
          return filename.includes('recaptcha');
        })
      ) {
        return null;
      }

      // Filter errors originating from GTM (Google Tag Manager)
      // GTM often triggers errors from third-party tracking scripts it loads
      if (
        stacktrace?.frames?.some((frame) => {
          const filename = frame.filename || '';
          return filename.includes('gtm.js') || filename.includes('gtag/js');
        })
      ) {
        return null;
      }
    }

    // Check for parentNode errors and filter if from third-party code
    if (event.exception?.values?.[0]) {
      const errorMessage = event.exception.values[0].value || '';
      const stacktrace = event.exception.values[0].stacktrace;

      // Check if this is a parentNode error
      const isParentNodeError =
        errorMessage.includes('parentNode') ||
        errorMessage.match(/null is not an object \(evaluating '.*\.parentNode'\)/) ||
        errorMessage.match(/Cannot read propert(y|ies) .* 'parentNode'/);

      if (isParentNodeError && stacktrace?.frames) {
        // Tag for tracking
        event.tags = event.tags || {};
        event.tags.error_type = 'parentNode_access';

        // Check if error is from our own code
        const hasOwnCodeInStack = stacktrace.frames.some((frame) => {
          const filename = frame.filename || '';
          return (
            // Next.js production bundles
            filename.includes('/_next/static/') ||
            filename.includes('/chunks/') ||
            // Turbopack development files
            filename.includes('turbopack:///[project]/src/') ||
            filename.includes('turbopack:///[project]/app/')
          );
        });

        // If no frames from our code, it's third-party - filter it out
        if (!hasOwnCodeInStack) {
          return null;
        }

        // Add context about where in the stack this occurred (for our own errors)
        const topFrame = stacktrace.frames[stacktrace.frames.length - 1];
        if (topFrame?.filename) {
          event.tags.error_location = topFrame.filename.split('/').pop() || 'unknown';
        }
      }
    }

    // Check User-Agent from multiple sources
    const userAgent =
      event.request?.headers?.['user-agent'] ||
      event.request?.headers?.['User-Agent'] ||
      navigator?.userAgent ||
      '';

    // Check browser name from context
    const browserName = event.contexts?.browser?.name || '';

    // Filter errors from Facebook/Instagram in-app browsers' injected scripts:
    //  - querySelector/addEventListener — manipulating DOM elements that may not exist
    //  - InvalidAccessError ("...does not support the operation...") — a native
    //    postMessage on a WKWebView bridge in an invalid state (teardown / hash nav).
    // Scoped to in-app browsers because InvalidAccessError is a generic DOMException
    // — filtering it globally could mask a real bug.
    if (event.exception?.values?.[0]) {
      const errorMessage = event.exception.values[0].value || '';
      const isFacebookBrowser =
        browserName === 'Facebook' ||
        userAgent.includes('FBAN/') ||
        userAgent.includes('FBAV/') ||
        userAgent.includes('Instagram');

      if (
        isFacebookBrowser &&
        (errorMessage.includes('querySelector') ||
          errorMessage.includes('addEventListener') ||
          errorMessage.includes('does not support the operation'))
      ) {
        return null;
      }
    }

    // Filter bot traffic (including monitoring tools, social media crawlers, and automation tools)
    if (
      !userAgent ||
      userAgent.match(
        /bot|crawler|spider|headless|phantomjs|selenium|puppeteer|playwright|prerender|pingdom|gtmetrix|statuscake|facebookexternalhit|twitterbot|linkedinbot|slackbot|whatsapp|telegrambot/i,
      )
    ) {
      return null;
    }

    // Filter headless browsers
    if (typeof browserName === 'string' && browserName.match(/headless/i)) {
      return null;
    }

    // Filter third-party errors (manual approach since thirdPartyErrorFilterIntegration
    // doesn't work with Turbopack)
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    if (frames && frames.length > 0) {
      // Hostname matches if it equals the suffix or ends with `.<suffix>`.
      // Substring matching on hostnames is unsafe (e.g. "evil.com/wayke.se").
      const hostnameMatches = (hostname: string, suffix: string): boolean =>
        hostname === suffix || hostname.endsWith(`.${suffix}`);

      const OWN_HOSTS = ['wayke.se', 'drive.no'];
      const THIRD_PARTY_HOSTS = [
        'googletagmanager.com',
        'google-analytics.com',
        'clarity.ms',
        'facebook.net',
      ];

      // Filter out Sentry's own frames and known third-party domains
      const relevantFrames = frames.filter((f) => {
        const filename = f.filename || '';

        // Skip Sentry's own frames
        if (
          filename.includes('@sentry/') ||
          filename.includes('node_modules/@sentry/') ||
          filename.includes('turbopack:///[project]/node_modules/@sentry/')
        ) {
          return false;
        }

        // Parse absolute URLs once and decide based on the hostname
        if (filename.startsWith('http://') || filename.startsWith('https://')) {
          try {
            const { hostname } = new URL(filename);

            // Skip known third-party hosts
            if (THIRD_PARTY_HOSTS.some((host) => hostnameMatches(hostname, host))) {
              return false;
            }

            // Only allow our own domain — drop everything else
            if (!OWN_HOSTS.some((host) => hostnameMatches(hostname, host))) {
              return false;
            }
          } catch {
            // If URL parsing fails, skip this frame
            return false;
          }
        }

        // Skip common third-party script path patterns (relative URLs)
        if (
          filename.includes('/gtm.js') ||
          filename.includes('/gtag/') ||
          filename.includes('/analytics.js')
        ) {
          return false;
        }

        return true;
      });

      // If no relevant frames left, it's likely injected code
      if (relevantFrames.length === 0) {
        return null;
      }

      // Check if any frame is from our own code (strict matching)
      const hasOwnCode = relevantFrames.some((frame) => {
        const filename = frame.filename || '';
        return (
          // Next.js production bundles
          filename.includes('/_next/static/') ||
          filename.includes('/chunks/') ||
          // Turbopack development files
          filename.includes('turbopack:///[project]/src/') ||
          filename.includes('turbopack:///[project]/app/') ||
          // App Router routes (but NOT simple script names like /gtm.js)
          (filename.startsWith('/') &&
            !filename.match(/^\/[^/]+\.(js|css)$/) && // Exclude /script.js patterns
            (filename.includes('/app/') ||
              filename.includes('/src/') ||
              filename.split('/').length > 3))
        );
      });

      // If no frames from our code, it's third-party
      if (!hasOwnCode) {
        return null;
      }
    }

    // captureConsoleIntegration forwards every console.error call. Only let React
    // hydration / recoverable-render errors through — drop everything else to
    // avoid drowning in 3rd-party console noise (e.g. photo-sphere-viewer logging
    // a CDN image 404 it already surfaces gracefully via panorama-error). Matches
    // the minified codes React emits (#418 mismatch, #421 suspended, #422 root,
    // #423 boundary, #425 text) and the dev-mode message text. The integration
    // marks these events with the top-level `event.logger`, not a tag.
    if (event.logger === 'console') {
      const message = event.message || event.exception?.values?.[0]?.value || '';
      const isReactError =
        /Minified React error #4(18|21|22|23|25)/.test(message) ||
        message.includes('Hydration failed') ||
        message.includes('did not match');
      if (!isReactError) {
        return null;
      }
    }

    return event;
  },
});

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
